import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Play, Save, ExternalLink, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { embedForUrl } from "../lib/videoEmbed";

// Admin-only editor for per-step walkthrough demo videos.
// The step IDs and labels must match the STEPS array in `lib/tour.jsx`; keeping
// them here as a static list means the admin can edit videos without redeploy,
// but adding a NEW step requires editing both files (rare event).
const STEP_LIST = [
  { id: "welcome", label: "Welcome" },
  { id: "add-account", label: "Add your money" },
  { id: "add-txn", label: "Log a spend" },
  { id: "done", label: "Finish 🎉" },
];

export default function TourVideos() {
  const { user } = useAuth();
  const [urls, setUrls] = useState({});         // committed (server) map
  const [drafts, setDrafts] = useState({});     // per-row draft edits
  const [saving, setSaving] = useState({});     // per-row saving flag
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tour-videos");
        setUrls(data || {});
        setDrafts(data || {});
      } catch (e) {
        toast.error("Failed to load videos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Non-admins get bounced. `is_admin` is populated by the /auth/me
  // entitlements block. Compute a flag; the actual redirect happens after
  // all hooks so we never violate the rules-of-hooks.
  const notAdmin = user && !user.is_admin;

  const setDraft = (stepId, value) =>
    setDrafts((prev) => ({ ...prev, [stepId]: value }));

  const saveOne = async (stepId) => {
    setSaving((s) => ({ ...s, [stepId]: true }));
    const raw = (drafts[stepId] || "").trim();
    try {
      await api.post("/admin/tour-videos", { step_id: stepId, video_url: raw });
      setUrls((prev) => {
        const next = { ...prev };
        if (raw) next[stepId] = raw;
        else delete next[stepId];
        return next;
      });
      toast.success(raw ? "Saved" : "Cleared");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving((s) => ({ ...s, [stepId]: false }));
    }
  };

  const clearOne = (stepId) => {
    setDraft(stepId, "");
    // fire save immediately so the row reflects the cleared state
    saveOne(stepId);
  };

  const anyDirty = useMemo(
    () =>
      STEP_LIST.some(
        (s) => (drafts[s.id] || "") !== (urls[s.id] || "")
      ),
    [drafts, urls]
  );

  if (notAdmin) return <Navigate to="/profile" replace />;

  return (
    <div className="max-w-2xl mx-auto p-4 pb-32">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/profile" className="nb-btn nb-btn-secondary !py-2 !px-2" data-testid="back-btn">
          <ArrowLeft size={16} strokeWidth={3} />
        </Link>
        <div>
          <div className="label !mb-0">Admin</div>
          <h1 className="font-display font-black text-2xl leading-tight">Walkthrough videos</h1>
        </div>
      </div>

      <p className="text-sm text-[#4a4a4a] font-medium mb-4 leading-snug">
        Paste a <b>Loom</b> or <b>YouTube</b> URL for any step to add a "▶ Watch demo" button
        to that step's tour card. Users see the video without leaving the app. Leave blank to
        remove.
      </p>

      <div className="nb-card-lg p-3 bg-white space-y-2" style={{ boxShadow: "6px 6px 0 0 #1a1a1a" }}>
        {loading ? (
          <div className="p-6 text-center font-bold text-sm text-[#4a4a4a]">Loading…</div>
        ) : (
          STEP_LIST.map((step, idx) => {
            const draft = drafts[step.id] ?? "";
            const committed = urls[step.id] ?? "";
            const dirty = draft.trim() !== committed.trim();
            const embed = embedForUrl(draft);
            const isValid = !draft.trim() || !!embed.src;
            return (
              <div
                key={step.id}
                data-testid={`tour-video-row-${step.id}`}
                className="p-3 rounded-lg border-2 border-[#1a1a1a]/10 hover:border-[#1a1a1a]/25 transition"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-[#1a1a1a] bg-white text-[10px] font-black tabular-nums">
                    {idx + 1}
                  </span>
                  <div className="flex-1 font-bold text-sm truncate">{step.label}</div>
                  {committed && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-[#12854a] bg-[#b5e4ca] px-2 py-0.5 rounded-full">
                      <CheckCircle2 size={10} strokeWidth={4} /> Live
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    data-testid={`tour-video-input-${step.id}`}
                    type="url"
                    placeholder="https://www.loom.com/share/..."
                    value={draft}
                    onChange={(e) => setDraft(step.id, e.target.value)}
                    className={`nb-input !py-1.5 !px-2 !text-xs flex-1 ${
                      !isValid ? "!border-[#c81e1e]" : ""
                    }`}
                  />
                  <button
                    data-testid={`tour-video-save-${step.id}`}
                    onClick={() => saveOne(step.id)}
                    disabled={!dirty || !isValid || saving[step.id]}
                    className="nb-btn nb-btn-dark !py-1.5 !px-2 !text-xs"
                    title={!isValid ? "URL not recognised" : "Save this row"}
                  >
                    <Save size={12} strokeWidth={3} />
                  </button>
                  {committed && (
                    <button
                      data-testid={`tour-video-clear-${step.id}`}
                      onClick={() => clearOne(step.id)}
                      className="nb-btn nb-btn-secondary !py-1.5 !px-2 !text-xs"
                      title="Remove the video from this step"
                    >
                      <Trash2 size={12} strokeWidth={3} />
                    </button>
                  )}
                  {embed.src && (
                    <a
                      href={embed.src}
                      target="_blank"
                      rel="noreferrer"
                      className="nb-btn nb-btn-secondary !py-1.5 !px-2 !text-xs"
                      title="Open this video in a new tab"
                    >
                      <ExternalLink size={12} strokeWidth={3} />
                    </a>
                  )}
                </div>
                {!isValid && draft && (
                  <div className="mt-1 text-[10px] font-bold text-[#c81e1e]">
                    Not a supported URL. Use Loom, YouTube, or a direct .mp4 link.
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {anyDirty && (
        <div
          data-testid="tour-video-dirty-hint"
          className="mt-3 px-3 py-2 rounded-lg border-2 border-dashed border-[#1a1a1a]/30 bg-[#fdecc8] text-xs font-bold flex items-center gap-1.5"
        >
          <Play size={12} strokeWidth={3} fill="currentColor" />
          You've got unsaved changes — hit <Save size={10} strokeWidth={4} className="inline" /> on each row to publish.
        </div>
      )}
    </div>
  );
}
